import {
  ClipboardList, Siren, HeartPlus, Wind, PhoneCall, ArrowRightLeft, ClipboardPen, CalendarDays, Wrench, UserCheck,
} from 'lucide-react';

/*
 * Ben Taub General OR / Trauma rotation. Built from the residency Blackboard course:
 * New Resident Orientation (June 2023), the New Resident's Handbook (June 2024),
 * the Trauma Senior Resident On-Call Guide, and the Protocols and Checklists folder
 * (ERAS, RPLND after bleomycin, PACU insulin). See docs/blackboard-ben-taub-or.md.
 *
 * Phones: Ben Taub extensions are 3-xxxx; the handbook gives the CON as 3-9002 and
 * 713-873-9002 from outside, which is the pattern used for every number here.
 */

export const BB_DOC = (id) => `https://bcm.blackboard.com/ultra/courses/_129_1/document/${id}?view=content&state=view`;
export const ext = (n) => `713873${n}`; // 3-xxxx -> 713-873-xxxx

export const benTaubOr = {
  accent: '#00697f',
  notesKey: 'notes:ben-taub:or-trauma',

  toolSections: [
    { title: 'Clinical tools', tools: [
      { id: 'trauma', title: 'Trauma Checklist', subtitle: 'Before arrival, on arrival, intraoperative', icon: ClipboardList, action: 'guide:trauma', featured: true },
      { id: 'stat', title: 'STAT Cases & MTP', subtitle: 'Rooms, stat block, massive transfusion, TXA', icon: Siren, action: 'guide:stat' },
      { id: 'eras', title: 'Enhanced Recovery', subtitle: 'Gynecology, colorectal, GU', icon: HeartPlus, action: 'guide:eras' },
      { id: 'rplnd', title: 'RPLND after Bleomycin', subtitle: 'Oxygen, fluids, monitoring', icon: Wind, action: 'guide:rplnd' },
      { id: 'call', title: 'When to Call', subtitle: 'What your attending must hear about', icon: PhoneCall, action: 'guide:call' },
      { id: 'handoff', title: 'Extubation & Handoffs', subtitle: 'Criteria, PACU, ICU, closing the case', icon: ArrowRightLeft, action: 'guide:handoff' },
    ] },
    { title: 'Logistics', tools: [
      { id: 'epic', title: 'Epic', subtitle: 'Consent, orders, blood, closing', icon: ClipboardPen, action: 'guide:epic' },
      { id: 'day', title: 'Your Day & Call', subtitle: 'First day, schedule, call, QGenda', icon: CalendarDays, action: 'guide:day' },
      { id: 'setup', title: 'OR Setup', subtitle: 'MSMAIDS, drugs, labeling, carts', icon: Wrench, action: 'guide:setup' },
      { id: 'senior', title: 'Senior On Call', subtitle: 'Running the board from 14:30', icon: UserCheck, action: 'guide:senior' },
    ] },
  ],

  contacts: {
    storageKey: 'ben-taub-or-favorites',
    groups: [
      { id: 'board', title: 'Board and seniors', open: true, items: [
        { label: 'CON, attending running the board', phone: ext('9002'), note: 'Late, sick, urgent questions, dismissal' },
        { label: 'Senior resident on call', phone: ext('9597'), note: 'Call shifts only' },
        { label: 'Front desk charge nurse', phone: ext('7250') },
      ] },
      { id: 'techs', title: 'Anesthesia techs', open: true, items: [
        { label: 'Anesthesia tech', phones: [{ label: '3-9020', phone: ext('9020') }, { label: '3-9018', phone: ext('9018') }] },
        { label: 'Code anesthesia tech', phone: ext('9023') },
        { label: '4th floor tech leader', phone: ext('9018') },
        { label: '2nd floor tech leader', phone: ext('7245') },
      ] },
      { id: 'support', title: 'Pharmacy and services', items: [
        { label: 'OR pharmacy', phone: ext('4400'), note: 'Brings meds to a 4th floor OR' },
        { label: 'Interpreter hotline', phone: ext('7300'), note: 'Second handset for the patient. Note the interpreter name and ID' },
        { label: 'Grand Rounds sign-in by text', phone: '7139037277', note: 'Text the event code' },
      ] },
    ],
  },

  locations: {
    maps: [],
    elevators: {},
    groups: [
      { id: 'or4', title: '4th floor ORs', items: [
        { name: 'OR board', floor: '4th floor', where: 'Hallway across from OR 8', note: '06:30 assignments, 14:50 call assignments, next-day assignments for the 06:30 huddle' },
        { name: 'Anesthesia offices and lounge', floor: '4th floor', where: 'Past the stat elevators, through the second double doors, badge in on the right', note: 'Lockers from Oscar Porter. Call room keys and Cisco phones in the copy-machine room next to his office' },
        { name: 'Preop holding', floor: '4th floor', note: 'Bay number matches the OR number. Spanish interpreter here in the mornings' },
        { name: 'Satellite pharmacy', floor: '4th floor', where: 'Back of preop holding', note: 'Narcotic and drug packs. Sign the narcotic sheet with your phone number' },
        { name: 'Anesthesia workroom', floor: '4th floor', where: 'Across from OR 8', note: 'Elective LMAs and supplies. Ultrasound machines outside' },
        { name: 'Fiberoptic cart and airway tower', floor: '4th floor', where: 'Across from OR 10' },
        { name: 'Blue Bell cart', floor: 'Every 4th floor OR', code: '243', codeLabel: 'Cart' },
      ] },
      { id: 'or2', title: '2nd floor ORs', items: [
        { name: 'STAT rooms', floor: '2nd floor', note: 'STAT cases to OR 1. Vascular to OR 2. Neuro to OR 3' },
        { name: 'Anesthesia workroom', floor: '2nd floor', where: 'Across from ORs 2 and 3', note: 'Ultrasound machines between ORs 4 and 5' },
        { name: 'Pyxis', floor: '2nd floor', note: 'Drugs and narcotics here rather than the satellite pharmacy. Small fridge in each OR' },
        { name: 'Blue Bell cart', floor: 'Every 2nd floor OR', code: '1234', codeLabel: 'Cart' },
      ] },
      { id: 'rooms', title: 'Call rooms', items: [
        { name: 'Call room 1', floor: '4th floor', where: 'Next to the anesthesia lounge' },
        { name: 'Call room 2', floor: '4th floor', where: 'Across from the anesthesia lounge' },
        { name: 'Call room 3', floor: '4th floor', where: 'OR hallway, across from OR 6' },
        { name: 'Call room 4', floor: '5B', note: 'Keycard' },
        { name: 'Senior call room', floor: '2nd floor', where: 'Hallway across from the front desk' },
      ] },
      { id: 'elsewhere', title: 'Elsewhere', items: [
        { name: 'Grand Rounds', floor: 'Main Baylor M112', note: 'Wednesdays 06:30' },
        { name: 'CA-1 lectures', floor: 'Main Baylor, Jaworski classroom', where: 'Main floor near the fountain security desk', note: 'Mondays and Wednesdays 16:00–18:00' },
        { name: 'EC shock room', floor: 'Emergency Center', note: 'Code 1 trauma activations, with the tech, STAT airway pack and airway tower' },
      ] },
    ],
  },

  documents: {
    storageKey: 'ben-taub-or-pinned-docs',
    groups: [
      { id: 'orientation', title: 'Orientation', items: [
        { title: 'New resident handbook', subtitle: 'June 2024, Blackboard, sign-in required', url: BB_DOC('_255041_1') },
        { title: 'New resident orientation', subtitle: 'Blackboard, sign-in required', url: BB_DOC('_255041_1') },
        { title: 'Senior on-call guide', subtitle: 'Blackboard, sign-in required', url: BB_DOC('_255041_1') },
      ] },
      { id: 'protocols', title: 'Protocols', items: [
        { title: 'Protocols and checklists', subtitle: 'ERAS, RPLND, insulin, COVID, blood ordering', url: BB_DOC('_255089_1') },
      ] },
      { id: 'tools', title: 'Tools', items: [
        { title: 'QGenda', subtitle: 'Your schedule', url: 'https://app.qgenda.com' },
        { title: 'ACGME case log', subtitle: 'MRN as the case ID', url: 'https://apps.acgme.org/connect/login' },
      ] },
    ],
  },

  guides: {
    trauma: {
      title: 'Trauma Checklist',
      intro: 'The wall poster, as written. Roles first, then the patient, then the case.',
      sections: [
        { title: 'Before the patient arrives', blocks: [{ type: 'steps', items: [
          'Assign roles: primary provider and airway, additional IV access, arterial line, charting',
          'Machine check: mask, vaporizers full, oxygen flows',
          'Airway: laryngoscope, ETT, oral airway, suction',
          'Medications ready: induction, specialty drugs, vasopressors, infusions',
          'IV fluids hanging. Arterial line set up',
          'Fluid warmer or Level One ready. Infusion pump available',
          'Stat blood checked',
        ] }] },
        { title: 'On arrival', blocks: [{ type: 'steps', items: [
          'Identify the patient. History if conscious',
          'Report from the surgeon: type of trauma, ED interventions and transfusions, type and cross sent, planned procedure',
          'Surgical time out',
          'Initiate MTP if criteria are met. See STAT Cases & MTP',
          'Tranexamic acid. Document why if not given',
          'Monitors. Preoxygenate. RSI',
          'Anesthetic: volatile, or ketamine, scopolamine and a benzodiazepine if unstable. Non-depolarising blockers',
        ] }] },
        { title: 'Intraoperative', blocks: [{ type: 'bullets', items: [
          'Prep, drape, incision. Antibiotics',
          'Additional IVs and the arterial line',
          'Transfuse 1:1:1: 6 units PRBC, 2 jumbo FFP, 1 apheresis platelet',
          'Send ABG, TEG, CBC, PT/PTT/INR, lactate',
          'Bair Hugger to normothermia',
          'Reassess the OGT, Foley, vent settings and the field',
          'Keep talking to the surgeons: bleeding, hypotension, pressors, labs',
        ] }, { type: 'rows', rows: [
          { label: 'Hgb under 7', value: 'Transfuse RBC' },
          { label: 'TEG R over 8', value: 'Jumbo FFP' },
          { label: 'TEG MA under 54', value: 'One jumbo platelet' },
        ] }] },
      ],
      sources: [{ title: 'Resident handbook, page 90', detail: 'Blackboard', href: BB_DOC('_255041_1') }],
    },

    stat: {
      title: 'STAT Cases & MTP',
      sections: [
        { title: 'When a STAT is called', blocks: [{ type: 'bullets', items: [
          'Called overhead. The senior phones every Cisco to make sure it was heard. Everyone available helps',
          'Go to the room named. Usually 2nd floor OR 1. Vascular goes to OR 2, neuro to OR 3',
          'If the 2nd floor is closed, one person fetches the gray stat box from pharmacy: pressors, calcium, bicarbonate, TXA',
          'Nursing brings the stat block, 4 units PRBC. Check it immediately',
          'Order STAT Block blood for every emergency case, used or not. The blood bank requires it',
          'Unknown COVID status: full PPE, the tech brings a metal table of essentials, runners outside the door',
        ] }] },
        { title: 'MTP criteria', blocks: [{ type: 'bullets', items: [
          'SBP under 65, or SBP 66–90 with HR over 110',
          'Plus any one of: on the third unit of PRBC, penetrating torso injury, major pelvic fracture, positive FAST',
        ] }, { type: 'note', text: 'Attending discretion applies. State "obstetric patient" if it is one.' }] },
        { title: 'Running MTP', blocks: [{ type: 'steps', items: [
          'Tell the circulator, who calls the blood bank directly',
          'Place the MTP order yourself. Blue arrow',
          'A nursing assistant collects the cooler with a patient sticker: 6 PRBC and 2 jumbo FFP, with 1 platelet kept at room temperature',
          'Blood runs through a blood set on a Hotline or the rapid infuser',
          'MTP continues until you tell the circulator to cancel it',
          'Chart stat block blood as STAT and MTP blood as MTP',
        ] }] },
        { title: 'Tranexamic acid', blocks: [{ type: 'rows', rows: [
          { label: 'Load', value: '1 g in 100 mL NS over 10 min' },
          { label: 'Infusion', value: '1 g in 500 mL NS over 8 h', note: 'On an Alaris' },
          { label: 'Where', value: 'Ziplock in the gray stat box' },
        ] }, { type: 'bullets', items: ['Hypotension if pushed fast', 'Not in the same line as blood, factor VIIa or penicillins'] }] },
        { title: 'Pressure bags and the rapid infuser', blocks: [{ type: 'bullets', items: [
          'Pressure bags behind the medication cart. Spike the blood to the blood set with saline on the other side, hang it inside, close the stopcock to atmosphere, pump. Open to deflate',
          'The rapid infuser is faster. A tech is usually there for STAT cases',
        ] }] },
      ],
      sources: [{ title: 'Resident handbook, pages 88–94 and 109', detail: 'Blackboard', href: BB_DOC('_255041_1') }],
    },

    eras: {
      title: 'Enhanced Recovery',
      intro: 'Opioid-sparing, normovolemic, early feeding and walking. The three protocols share most steps.',
      segments: [
        { id: 'common', label: 'Shared', sections: [
          { title: 'Holding', items: [
            'Gabapentin 100–300 mg PO and tramadol 50 mg PO on arrival',
            'Acetaminophen 1 g IV once the IV is in',
            'Epidural or TAP block by the pain team',
            'LR at 50 mL/h',
            'Diabetics: glucose under 200 before the OR, IV insulin if not',
            'Forced-air warming in holding',
          ] },
          { title: 'Intraoperative', items: [
            'Avoid morphine and hydromorphone. Epidural with small fentanyl boluses',
            'No epidural or TAP: ketamine 10–20 mg hourly, or 0.2–0.5 mg/kg then 2 mcg/kg/min. And or lidocaine 1–2 mg/kg at induction then 1–2 mg/min',
            'Heparin 5000 units SC after induction. SCDs',
            'PONV prophylaxis by risk: dexamethasone 4 mg, ondansetron 4 mg, scopolamine',
            'Crystalloid 1–3 mL/kg/h. Albumin for volume. SVV or PPV over 14%: 200 mL boluses',
            'No OG suction for an hour after the oral meds',
            'Temperature 36–38. FiO2 80% or more',
            'End: ketorolac 15–30 mg if the surgeon agrees. TAP or QL block for laparotomy without an epidural, surgeon infiltration for laparoscopy',
          ] },
          { title: 'Postoperative', items: [
            'Acetaminophen 1 g IV q6h for three doses, then oral',
            'Epidural managed by APMS',
            'Scheduled gabapentin and tramadol, scheduled NSAID or ketorolac prn',
            'Morphine or hydromorphone in small doses for breakthrough only',
            'Out of bed the evening of surgery. Chewing gum allowed',
            'Confirm DVT prophylaxis is ordered with an epidural in place',
          ] },
        ] },
        { id: 'gyn', label: 'Gynecology', sections: [
          { title: 'Hysterectomy, abdominal and laparoscopic', items: [
            'Forte TID for 5 days before surgery in GYN oncology patients',
            'Carbohydrate drink 25 g at 9 pm and 25 g at 5 am',
            'Heparin in the holding area rather than after induction',
            'Epidural or TAP for laparotomy only',
            'Ambulation: 2 hours out of bed the first evening, 8 hours a day after',
          ] },
        ] },
        { id: 'colorectal', label: 'Colorectal', sections: [
          { title: 'Differences', items: [
            'Bowel prep and oral antibiotic the day before',
            'TAP block with Exparel',
            'Antibiotics: ceftriaxone with metronidazole, or ertapenem. No redose',
            'Sparing with fluid until the anastomosis is complete',
            'Ambulation checked on APMS rounds with physical therapy',
          ] },
        ] },
        { id: 'gu', label: 'GU', sections: [
          { title: 'Cystectomy and nephrectomy', items: [
            'Cardiology clearance with echo and stress test. Pulmonary if COPD or asthma',
            'No bowel prep. IMPACT AR three times a day for 5–7 days',
            'No tramadol: naproxen or meloxicam if the kidneys allow, agreed with the surgeon',
            'Alvimopan 12 mg 30–60 minutes before, unless on chronic opioids',
            'Antibiotic: ertapenem 1 g',
            'Volume with albumin or PRBC over crystalloid. MAP over 65. Hgb over 8, transfusing as little as possible',
            'OG required, out before extubation. Mask oxygen to SpO2 over 95',
            'Epidural out at 24–36 hours. Alvimopan twice daily for 7 days',
          ] },
        ] },
      ],
      sources: [{ title: 'ERAS protocols', detail: 'Blackboard', href: BB_DOC('_255089_1') }],
    },

    rplnd: {
      title: 'RPLND after Bleomycin',
      intro: 'Post-chemotherapy retroperitoneal lymph node dissection after bleomycin. The lungs cannot take oxygen; the circulation cannot take crystalloid. September 2014.',
      sections: [
        { title: 'Oxygen', blocks: [{ type: 'bullets', items: [
          'Reassuring airway: preoxygenate at FiO2 under 0.3, mask ventilate at 0.3 or less, ideally room air',
          'Difficult airway, asleep: 0.3 then four vital-capacity breaths at 1.0, back under 0.3 once ventilating',
          'Awake intubation: keep FiO2 under 0.3 while preparing',
          'Throughout: FiO2 under 0.25 unless PaO2 is under 65. Target PaO2 65–80 at the lowest FiO2, or SpO2 90–95 without gases',
        ] }] },
        { title: 'Monitoring and analgesia', blocks: [{ type: 'bullets', items: [
          'Arterial line and central line for everyone. EV1000 for CO, CI, SVV, SVR. PPV from the pulse oximeter',
          'Offer an epidural. Run it at half to full maintenance after induction, no concentrated bolus. Stop it if the pressure drops and restart at closing',
          'Pressors for the blood pressure: norepinephrine or phenylephrine',
          'No epidural: intra-op narcotics and acetaminophen',
        ] }] },
        { title: 'Fluids', blocks: [{ type: 'rows', rows: [
          { label: 'Maintenance', value: 'Half rate, balanced salt on a pump' },
          { label: 'Targets', value: 'CI 2.2–4.0, PPV under 13, SVR 800–1200', note: 'Urine 0.3 mL/kg/h once the ureters are free' },
          { label: 'CI 2.0–2.2, PPV over 10, SVR over 1200, low urine, base deficit over 8', value: 'Albumin 250–500 mL' },
          { label: 'CI under 2.0, PPV over 15, SVR over 1500, base deficit over 12', value: 'Albumin 250–500 mL plus 250 mL balanced salt' },
          { label: 'Transfuse', value: 'Hgb under 8', note: 'Or ischemic ST changes with Hgb 8–10' },
        ] }] },
        { title: 'Preop fluids', blocks: [{ type: 'bullets', items: [
          'NPO solids after midnight. Clears until 05:30 unless an aspiration risk',
          'Aspiration risk inpatient: IV at half maintenance from 10 pm. Outpatient: half the deficit as balanced salt before incision',
          'Not first case: an IV by 06:00 at half maintenance',
        ] }] },
      ],
      sources: [{ title: 'PC-RPLND guideline', detail: 'Blackboard', href: BB_DOC('_255089_1') }],
    },

    call: {
      title: 'When to Call',
      intro: 'Your attending is present for every induction and extubation. In between, call for any of these.',
      sections: [
        { title: 'Always', blocks: [{ type: 'bullets', items: [
          'Before induction and before extubation',
          'Before any procedure: peripheral IVs, arterial lines, central lines',
          'Before starting any infusion',
          'Before giving albumin. Contraindicated for some Jehovah\'s Witness patients, and the attending needs to hear about the volume deficit',
          'Before rolling back. Never leave preop without having discussed the patient',
        ] }] },
        { title: 'Changes', blocks: [{ type: 'bullets', items: [
          'MAP or systolic more than 20% off baseline',
          'Heart rate more than 20% off baseline',
          'Arrhythmia or ST change',
          'SpO2 under 97% in a patient who started at 100',
          'Acute blood loss or need for transfusion',
          'Temperature under 35 °C',
          'Rising airway pressures',
          'Laparoscopic converting to open',
          'Any question about a dose, or a medication error',
        ] }] },
        { title: 'How', blocks: [{ type: 'bullets', items: [
          'Patient communication with attendings goes over Teams. Check the preference sheet for text versus call',
          'The OR phone is on the anesthesia machine: 3-965 plus the OR number on the 4th floor, 3-725 plus the OR number on the 2nd',
          'Cannot reach the attending: call the CON at 3-9002',
          'Emergency: the circulator calls Anesthesia Stat overhead. To call overhead yourself dial *801, wait for the beep, 129, wait for the beep, speak',
        ] }] },
      ],
      sources: [{ title: 'Resident handbook, page 82', detail: 'Blackboard', href: BB_DOC('_255041_1') }],
    },

    handoff: {
      title: 'Extubation & Handoffs',
      sections: [
        { title: 'Extubation criteria', blocks: [{ type: 'rows', rows: [
          { label: 'Oxygenation', value: 'SpO2 over 95, PaO2 over 60' },
          { label: 'Ventilation', value: 'TV over 5 mL/kg, RR 8–30, EtCO2 under 50' },
          { label: 'Reversal', value: 'TOF over 0.9, head lift over 5 s' },
          { label: 'Acid-base', value: 'pH over 7.25' },
          { label: 'And', value: 'Warm, stable, follows commands' },
        ] }, { type: 'bullets', items: [
          'Airway supplies ready for reintubation. OG out on suction. Oral airway in for the bite',
          'Loosen the tape, deflate the cuff, APL to about 30, positive-pressure breath as the tube comes out, APL back to zero, mask on',
          'Hold the tube while they wake. Watch and talk to the patient the whole way to PACU',
        ] }] },
        { title: 'Intraoperative relief', items: ['Click the Handoff button on the record and go through IPASS. Tick the boxes as you cover them'] },
        { title: 'PACU', items: [
          'Help put the monitors on, then report to the PACU nurse through their handoff sheet',
          'The PACU resident is there too. Tell them about anything that needs watching',
          'Postprocedure: chart vitals, complete Post Evaluation tabs 1 and 2. Tab 4 is the attending\'s',
          'Print the narcotics discrepancy sheet and take it to pharmacy with the pack. 2nd floor: waste with a witness in the Pyxis',
        ] },
        { title: 'ICU', items: [
          'Transport monitor. Take every drug, especially pressors',
          'Report to the nurse and the SICU resident. Note the ICU vitals for the chart',
          'Complete Post Evaluation tabs 1 to 3',
          'Separate post-op note within 48 hours, with the attending who finished the case as co-signer',
          'Put the patient on the ICU follow-up list',
        ] },
      ],
      sources: [{ title: 'Resident handbook, pages 85–88', detail: 'Blackboard', href: BB_DOC('_255041_1') }],
    },

    epic: {
      title: 'Epic',
      segments: [
        { id: 'preop', label: 'Pre-op', sections: [
          { title: 'Ready for the OR', blocks: [{ type: 'steps', items: [
            'Preprocedure tab: complete the pre-evaluation with an airway exam that mentions teeth',
            'Obtain consent. Then at the bottom of the tab click Consent Complete and Review and Sign',
            'Click "Patient has been seen by Anesthesia" and "Anesthesia Ready for OR". Without these the delay is charged to us',
          ] }] },
          { title: 'Electronic consent', blocks: [{ type: 'steps', items: [
            'Electronic Consents in the Preprocedure navigator, then Add',
            'New ANES ECONSENT, or the SPANISH form',
            'Your Harris Health ID and name. The person logged in must be the one obtaining consent',
            'Preferred language. If not English, the interpreter\'s name and ID',
            'Planned procedure, then every anesthesia method discussed. Everyone is consented for general, even for MAC, and nothing can be added after signing',
            'Witness, usually the preop nurse. Cannot be changed once anyone has signed',
            'Patient signs first, then you, then the witness. Any change means a new consent',
          ] }] },
          { title: 'Pre-op orders', items: [
            'Order set "ane pre-op", worth adding to favorites. Uncheck what does not apply, such as the pregnancy test for men',
            'Extra labs only after talking to the attending',
            'Multimodal oral analgesia the day before if the attending agrees',
          ] },
        ] },
        { id: 'intraop', label: 'Intra-op', sections: [
          { title: 'Starting', items: ['Anesthesia Start, Start data collection, scan the armband as soon as you are in the room'] },
          { title: 'Blood', blocks: [{ type: 'steps', items: [
            'Order set "IP BB ADULT BLOOD BANK & TRANSFUSION ORDERS"',
            'First type and screen in Harris Health: add ABO/Rh Confirmation and send two samples. Valid 72 hours',
            'Prepare RBCs Panel: uncheck "Transfuse, nursing" because you transfuse, priority STAT, number of units, sign',
            'Tell the circulator to send for it',
            'Check with a second person: name, MRN, DOB, blood type, unit number, expiry against the pink slip. Both sign with title and ID',
            'Chart: Blood event, choose STAT block, MTP or crossmatched correctly, scan unit number and product code, Finish Assignments',
          ] }] },
        ] },
        { id: 'postop', label: 'Post-op', sections: [
          { title: 'Orders', items: [
            'PACU order set "ane recovery". Nursing orders are pre-checked',
            'Add prn pain and antiemetics as agreed with the attending. No extra acetaminophen or NSAID if given in the OR',
          ] },
          { title: 'Closing', items: [
            'Postprocedure, Vitals, then Post Evaluations: tabs 1–2 for PACU, 1–3 for ICU',
            'Anesthesia Stop',
            'ICU patients: a separate post-op note within 48 hours, attending as co-signer',
          ] },
          { title: 'Case log', items: [
            'ACGME log with the MRN as the case ID. E and life-threatening for emergencies',
            'LMA is supraglottic. C-MAC or GlideScope is indirect laryngoscopy. Mask only if mask was the whole case',
          ] },
        ] },
      ],
      sources: [{ title: 'Resident handbook', detail: 'Blackboard, June 2024', href: BB_DOC('_255041_1') }],
    },

    day: {
      title: 'Your Day & Call',
      segments: [
        { id: 'day', label: 'The day', sections: [
          { title: 'First day', items: [
            'Badge, Epic, Pyxis and scrub access working before you start. Parking tag from security',
            '06:15 the first day, 06:30 every other day, at the OR board across from OR 8',
            'Mentor Mode for the first weeks: paired with another resident',
          ] },
          { title: 'Every day', blocks: [{ type: 'rows', rows: [
            { label: '06:30', value: 'Board', note: 'Check the schedule. Relieve an overnight case first' },
            { label: '07:00', value: 'Set up', note: 'After seeing, consenting and cannulating the patient' },
            { label: '07:20', value: 'Ready to roll', note: 'Surgeon marked, circulator signed, attending seen, consents done' },
            { label: '07:30', value: 'Cases start', note: '08:30 on Wednesdays after Grand Rounds' },
            { label: '15:30', value: 'Usually relieved', note: 'Then pre-op tomorrow\'s patients' },
            { label: 'By 20:00', value: 'Call the attending', note: 'Teams, text or call per the preference sheet' },
          ] }, { type: 'bullets', items: [
            'Breaks: 15 minutes in the morning, 30 for lunch',
            'Everyone is dismissed by the CON. Nobody leaves the 4th floor without telling faculty and the CON',
            'Late without calling makes you the Z person, last to leave. Sick: call the CON and email the residency office',
          ] }] },
          { title: 'Conferences', items: [
            'Grand Rounds Wednesdays 06:30, M112 Main Baylor. Sign in by text with the event code. Excused if on call that day',
            'CA-1 lectures Mondays and Wednesdays 16:00–18:00, Jaworski classroom. Excused if post-call',
          ] },
        ] },
        { id: 'call', label: 'Call', sections: [
          { title: 'Hours', blocks: [{ type: 'rows', rows: [
            { label: 'Weekdays', value: '14:30 to 06:30', note: '07:30 on Wednesdays' },
            { label: 'Weekend and holiday AM', value: '07:00 to 17:00' },
            { label: 'Weekend and holiday PM', value: '17:00 to 07:00' },
          ] }] },
          { title: 'Arriving', items: [
            '14:30 at the whiteboard outside OR 8. The CON hands inpatient consents to the post-call team',
            'Pick up a Cisco phone in the copy-machine room. Give the number to the senior and write it on the board',
            '14:50 back at the board for room assignments. In the room to relieve by 15:00',
            'Meal tickets. Do not be late; the garage is slow in the afternoon',
          ] },
          { title: 'During', items: [
            'Set up, pre-op and consent, then discuss the plan with the senior and the attending before rolling back',
            'After 19:00 and on weekends: two rooms and a stat room',
            'STAT cases are called overhead and the senior calls every phone. Everyone available helps',
            'Return call room keys and Cisco phones at the end',
          ] },
        ] },
        { id: 'schedule', label: 'Schedule', sections: [
          { title: 'Requests', items: [
            'QGenda, app or app.qgenda.com. Questions to Dr Kevin Tzan',
            'Requests by the 10th of the preceding month through OpenTempo. Three a month at most',
            'Four free days a month. Two weekends off is a bonus, not a promise',
            'Post-call from another rotation on your first day: tell the scheduler as soon as you know',
            'Vacation is easiest August to December',
          ] },
          { title: 'QGenda codes', blocks: [{ type: 'table', cols: ['Code', 'Means'], rows: [
            ['rD', 'OR day'], ['rC', 'Junior on call'], ['rC3', 'CA-3 on call'], ['rX', 'Post call'], ['rAM / rPM', 'Weekend AM or PM call'],
            ['Late', 'Stays to finish cases'], ['rZ', 'Last of the late people'], ['rSW', 'Swing, 11:00 to 19:00'], ['rF', 'Free day'], ['rNC', 'No call'],
            ['rPS', 'Preop screening'], ['rRR', 'Recovery room'], ['rSS', 'Senior seminar'], ['rR', 'Reading day, report 06:30'], ['rMM', 'Mentor mode'],
            ['rCV / rCV3', 'Cardiac room, CA-1 or CA-3'], ['rN1', 'Neuro room'], ['rY', 'Eye room'], ['rRG', 'Regional'], ['rREM', 'Remote'], ['rAW', 'Airway month'],
          ] }] },
        ] },
      ],
      sources: [
        { title: 'New resident orientation', detail: 'Blackboard, June 2023', href: BB_DOC('_255041_1') },
        { title: 'Resident handbook', detail: 'Blackboard, June 2024', href: BB_DOC('_255041_1') },
      ],
    },

    setup: {
      title: 'OR Setup',
      intro: 'MSMAIDS. Ambu bag behind the Blue Bell cart, E-cylinders checked, then down the list.',
      sections: [
        { title: 'Machine and suction', items: [
          'Cycle the power and run the full self-check',
          'Three vaporizers: desflurane blue, isoflurane purple, sevoflurane yellow. Refill each morning',
          'Press the white circle button all the way down before turning a vaporizer. Half way breaks it',
          'Suction: canister, tubing, Yankauer. Tested, on, within reach for induction',
        ] },
        { title: 'Monitors and airway', items: [
          'Standard ASA. Pulse-ox stickers and cuffs in the Blue Bell cart. EKG pads can go on in holding',
          'Two laryngoscope handles on the chargers, Mac and Miller blades on the bench. Video laryngoscope or fiberoptic from the tech, early',
          '7.0 for women, 8.0 for men. Stylet in a hockey stick. Cuff checked. Oral airway sized',
          'Airway equipment stays sealed until the patient is in the room',
          'The LMAs in the bottom drawer are rescue only. Elective LMAs come from the workroom',
          'Bougie above the workstation or on the Blue Bell cart',
        ] },
        { title: 'Drugs', blocks: [{ type: 'bullets', items: [
          '4th floor: narcotic and drug pack from the satellite pharmacy after setup. Sign the sheet with your phone number. Everything else is in the tray in the Blue Bell cart',
          '2nd floor: Pyxis for everything, small fridge in the OR',
          'Draw up induction and emergency drugs only. Nothing in your pockets. Lock the cart when you step out. Discard leftovers before you leave',
          'Alcohol-wipe every vial and ampule neck',
        ] }, { type: 'rows', rows: [
          { label: 'Midazolam', value: '3 mL syringe' },
          { label: 'Fentanyl', value: '3–5 mL' },
          { label: 'Lidocaine', value: '5 mL' },
          { label: 'Rocuronium or vecuronium', value: '5–10 mL', note: 'One blocker only' },
          { label: 'Propofol', value: '20 mL', note: 'Expires 6 hours after opening. Write the time' },
        ] }] },
        { title: 'Labeling', items: [
          'Every syringe: drug and concentration, expiry, your initials. Sharpie on the Blue Bell cart',
          'Succinylcholine, ephedrine and phenylephrine arrive premade',
        ] },
        { title: 'Alaris pumps', items: [
          'Ask the circulator to order one. System On, then Options, Anesthesia Mode, Enable, so pauses do not alarm',
          'Spike and prime, flat handle out, tubing in, close. Channel Select, Guardrail Drugs, pick the drug, confirm dose and concentration',
          'Restart runs it. Pause pauses. Hold Channel Off to stop',
        ] },
      ],
      sources: [{ title: 'Resident handbook, pages 38–49 and 95', detail: 'Blackboard', href: BB_DOC('_255041_1') }],
    },

    senior: {
      title: 'Senior On Call',
      intro: 'The CA-3 runs the afternoon dismissal and the night. From the Trauma Senior Resident On-Call Guide.',
      sections: [
        { title: 'Afternoon', blocks: [{ type: 'steps', items: [
          '14:30: run the OR list with the front desk charge nurse. Time left per room, cases to follow, rooms closing',
          'Write a dismissal plan. Meet the CON at 14:45',
          'Assignments on the whiteboard outside OR 8. Whole team at the board at 14:50. Relief in rooms by 15:00',
          'Release the PACU resident at 15:30, or have them in a room by then. Same for PACT',
          'Check in with the CON at least hourly until only the call team is left',
        ] }] },
        { title: 'Order of release', blocks: [{ type: 'bullets', items: [
          'Day people first, lecture-day trainees before anyone: CA-1s on Mondays and Wednesdays, SRNAs on Thursdays',
          'Then the last-numbered CRNA, then Lates, then Z, then Swing at about 19:00',
          'Call the CON before relieving Late, Z or Swing',
          'GI residents and CRNAs are outside the process. Ask the CON about offsite cases. Locums CRNAs have fixed end times',
          'A CRNA and SRNA pair can be split at 15:00. Leave the SRNA in the case',
          'Everyone calls you within 10 minutes of closing their last record',
        ] }, { type: 'rows', rows: [
          { label: '15:00', value: '12 rooms running' },
          { label: '17:00', value: '8 rooms' },
          { label: '19:00', value: '3 rooms' },
        ] }] },
        { title: 'Night', blocks: [{ type: 'bullets', items: [
          'The PACU resident is the STAT person until 15:30. After that there are no floats: you do STAT cases with the attending',
          'Every anesthetic is discussed with the trauma attending, who is present for inductions and extubations',
          'Code 1 trauma: you go to the EC shock room with the tech, the STAT airway pack and the airway tower',
          'Code Blue: you respond with the STAT airway pack, the tech and tower, and another team member if free',
          'Overnight cases rotate for equity. The attending may adjust for acuity',
          'Next day\'s assignments on the whiteboard before the 06:30 huddle. Keys and phones returned',
        ] }] },
      ],
      sources: [{ title: 'Senior on-call guide', detail: 'Blackboard', href: BB_DOC('_255041_1') }],
    },
  },
};
